<script lang="ts">
export default {
  name: 'PromptHistoryModal',
};
</script>

<script setup lang="ts">
import { ref } from 'vue';
import { DateTime } from 'luxon';
import AppModal from '@/components/v2/AppModal.vue';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import Badge from '@/components/ui/Badge.vue';
import Button from '@/components/ui/UIButton.vue';
import Separator from '@/components/ui/Separator.vue';
import { Copy, Clock, FileText, Cpu, User, CheckCircle, AlertCircle, Loader } from 'lucide-vue-next';
import type { AIPromptResult } from '@/types/ai';
import { aiService } from '@/services/AIService';

const isVisible = ref(false);
const isLoading = ref(false);
const error = ref<string>('');
const promptResults = ref<AIPromptResult[]>([]);
const selectedResult = ref<AIPromptResult | null>(null);
const expandedPrompts = ref<Set<number>>(new Set());
const expandedOutputs = ref<Set<number>>(new Set());
const copySuccess = ref<Set<number>>(new Set());

const show = () => {
  isVisible.value = true;
  loadPromptHistory();
};

const hide = () => {
  isVisible.value = false;
  selectedResult.value = null;
  expandedPrompts.value.clear();
  expandedOutputs.value.clear();
  copySuccess.value.clear();
};

const loadPromptHistory = async () => {
  isLoading.value = true;
  error.value = '';

  try {
    promptResults.value = await aiService.getPromptResults();
  } catch (err: any) {
    error.value = err.message || 'Failed to load prompt history';
    promptResults.value = []; // Reset to empty array on error
    console.error('Failed to load prompt history:', err);
  } finally {
    isLoading.value = false;
  }
};

const formatDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
};

const truncateText = (text: string, maxLength: number = 100) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const togglePromptExpanded = (resultId: number) => {
  const expanded = new Set(expandedPrompts.value);
  if (expanded.has(resultId)) {
    expanded.delete(resultId);
  } else {
    expanded.add(resultId);
  }
  expandedPrompts.value = expanded;
};

const toggleOutputExpanded = (resultId: number) => {
  const expanded = new Set(expandedOutputs.value);
  if (expanded.has(resultId)) {
    expanded.delete(resultId);
  } else {
    expanded.add(resultId);
  }
  expandedOutputs.value = expanded;
};

const copyResult = async (result: AIPromptResult) => {
  try {
    // Try to parse the rawOutput as JSON and format it, otherwise use as-is
    let textToCopy = result.rawOutput;
    try {
      const parsedOutput = JSON.parse(result.rawOutput);
      textToCopy = JSON.stringify(parsedOutput, null, 2);
    } catch {
      // If parsing fails, copy the raw output as-is
      textToCopy = result.rawOutput;
    }

    await navigator.clipboard.writeText(textToCopy);
    const newCopySuccess = new Set(copySuccess.value);
    newCopySuccess.add(result.id);
    copySuccess.value = newCopySuccess;

    setTimeout(() => {
      const updatedCopySuccess = new Set(copySuccess.value);
      updatedCopySuccess.delete(result.id);
      copySuccess.value = updatedCopySuccess;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy result:', err);
  }
};

const getModelDisplayName = (modelArn: string) => {
  // Extract a readable model name from the ARN
  const parts = modelArn.split('/');
  return parts[parts.length - 1] || modelArn;
};

defineExpose({
  show,
  hide,
});
</script>

<template>
  <AppModal
    :show="isVisible"
    title="Prompt Execution History"
    :handle-cancel="hide"
    :handle-action="hide"
    action-button-label="Close"
    :modal-class="'tw:w-full tw:max-w-4xl'"
    :show-overflow="false">
    <template #content>
      <div class="tw:space-y-4">
        <!-- Loading State -->
        <div v-if="isLoading" class="tw:flex tw:items-center tw:justify-center tw:py-8">
          <Loader class="tw:mr-2 tw:h-6 tw:w-6 tw:animate-spin tw:text-blue-600" />
          <span class="tw:text-slate-600">Loading prompt history...</span>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="tw:flex tw:items-center tw:justify-center tw:py-8">
          <AlertCircle class="tw:mr-2 tw:h-6 tw:w-6 tw:text-red-500" />
          <span class="tw:text-red-600">{{ error }}</span>
        </div>

        <!-- Empty State -->
        <div v-else-if="promptResults && promptResults.length === 0" class="tw:py-12 tw:text-center">
          <Clock class="tw:mx-auto tw:mb-4 tw:h-12 tw:w-12 tw:text-slate-300" />
          <h3 class="tw:mb-2 tw:text-lg tw:font-medium tw:text-slate-700">No History Yet</h3>
          <p class="tw:mx-auto tw:max-w-sm tw:text-sm tw:text-slate-500">
            Execute prompts to see your execution history here. All your past prompt results will be stored and
            accessible.
          </p>
        </div>

        <!-- Results List -->
        <div v-else-if="promptResults && promptResults.length > 0" class="tw:relative">
          <!-- Scrollable content -->
          <div class="tw:max-h-[60vh] tw:space-y-4 tw:overflow-y-auto tw:p-3">
            <div v-for="result in promptResults" :key="result.id">
              <Card class="tw:hover:shadow-md tw:border-slate-200 tw:bg-white tw:transition-shadow">
                <CardContent class="tw:space-y-3">
                  <CardTitle class="tw:mt-2 tw:flex tw:items-center tw:justify-between tw:text-sm">
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <Clock class="tw:h-4 tw:w-4 tw:text-slate-500" />
                      <span class="tw:text-slate-600">{{ formatDate(result.createdAt) }}</span>
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        @click="copyResult(result)"
                        :class="copySuccess.has(result.id) ? 'tw:border-green-300 tw:bg-green-50' : ''">
                        <template v-if="copySuccess.has(result.id)">
                          <CheckCircle class="tw:mr-1 tw:h-3 tw:w-3 tw:text-green-600" />
                          Copied!
                        </template>
                        <template v-else>
                          <Copy class="tw:mr-1 tw:h-3 tw:w-3" />
                          Copy JSON
                        </template>
                      </Button>
                    </div>
                  </CardTitle>

                  <!-- Metadata Row -->
                  <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:text-xs">
                    <div class="tw:flex tw:items-center tw:gap-1">
                      <User class="tw:h-3 tw:w-3 tw:text-purple-500" />
                      <Badge variant="outline" class="tw:px-2 tw:py-1 tw:text-xs">
                        {{ result.userEmail }}
                      </Badge>
                    </div>

                    <div class="tw:flex tw:items-center tw:gap-1">
                      <Cpu class="tw:h-3 tw:w-3 tw:text-blue-500" />
                      <Badge variant="secondary" class="tw:px-2 tw:py-1 tw:text-xs">
                        {{ getModelDisplayName(result.modelArn) }}
                      </Badge>
                    </div>

                    <div v-if="result.documentName" class="tw:flex tw:items-center tw:gap-1">
                      <FileText class="tw:h-3 tw:w-3 tw:text-green-500" />
                      <Badge variant="outline" class="tw:px-2 tw:py-1 tw:text-xs">
                        {{ result.documentName }}
                      </Badge>
                    </div>
                  </div>

                  <Separator />

                  <!-- Prompt Message -->
                  <div class="tw:space-y-2">
                    <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Prompt</h4>
                    <div class="tw:rounded-md tw:border tw:bg-slate-50 tw:p-3">
                      <p class="tw:font-mono tw:text-sm tw:text-slate-600">
                        {{
                          expandedPrompts.has(result.id)
                            ? result.promptMessage
                            : truncateText(result.promptMessage, 150)
                        }}
                      </p>
                      <Button
                        v-if="result.promptMessage.length > 150"
                        variant="ghost"
                        size="sm"
                        @click="togglePromptExpanded(result.id)"
                        class="tw:mt-2 tw:h-6 tw:px-2 tw:text-xs">
                        {{ expandedPrompts.has(result.id) ? 'Show Less' : 'Show More' }}
                      </Button>
                    </div>
                  </div>

                  <!-- Output Preview -->
                  <div class="tw:space-y-2">
                    <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Output Preview</h4>
                    <div class="tw:rounded-md tw:border tw:bg-slate-50 tw:p-3">
                      <pre class="tw:whitespace-pre-wrap tw:font-mono tw:text-xs tw:text-slate-600">{{
                        expandedOutputs.has(result.id) ? result.rawOutput : truncateText(result.rawOutput, 300)
                      }}</pre>
                      <Button
                        v-if="result.rawOutput.length > 300"
                        variant="ghost"
                        size="sm"
                        @click="toggleOutputExpanded(result.id)"
                        class="tw:mt-2 tw:h-6 tw:px-2 tw:text-xs">
                        {{ expandedOutputs.has(result.id) ? 'Show Less' : 'Show More' }}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <Button @click="hide" class="tw:mr-2">Close</Button>
    </template>
  </AppModal>
</template>
